import React, { useState } from 'react';
import styled from 'styled-components';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';

import ChevronIcon from 'src/assets/chevron-icon';
import useTranslation from 'hooks/use-translation';
import { Typography } from 'src/typography';

export function Faq(): JSX.Element {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: t('dutchiePay.signup.faq.whatIs.question', 'What is Pay by Bank?'),
      answer: t(
        'dutchiePay.signup.faq.whatIs.answer',
        'Pay by Bank (PBB) is an ACH-based payment solution that allows customers to purchase cannabis directly from their bank account — no cash, no card swipe required.'
      ),
    },
    {
      question: t('dutchiePay.signup.faq.safe.question', 'Is Pay by Bank safe?'),
      answer: t(
        'dutchiePay.signup.faq.safe.answer',
        'Yes. The bank verification process uses industry-standard security and leading technology partners to protect your account information.'
      ),
    },
    {
      question: t('dutchiePay.signup.faq.bankLogin.question', 'Does the retailer see my bank login?'),
      answer: t(
        'dutchiePay.signup.faq.bankLogin.answer',
        "No. Your credentials are entered only on your bank's own page and are never shared with the retailer."
      ),
    },
    {
      question: t('dutchiePay.signup.faq.forced.question', 'Will I be forced to use Pay by Bank for all purchases?'),
      answer: t(
        'dutchiePay.signup.faq.forced.answer',
        'No. You are able to choose how you pay for each order and can always choose to use a different payment source.'
      ),
    },
    {
      question: t('dutchiePay.signup.faq.timing.question', 'When will the money be removed from my bank account?'),
      answer: t(
        'dutchiePay.signup.faq.timing.answer',
        'Funds are typically withdrawn by the end of the day or the following business day, depending on when you place your order. Please ensure you have sufficient funds available in your account at the time of purchase to avoid any processing issues.'
      ),
    },
    {
      question: t('dutchiePay.signup.faq.contact.question', 'Who do I contact if I have an issue with my payment?'),
      answer: t(
        'dutchiePay.signup.faq.contact.answer',
        'Reach out to the dispensary where you made your purchase — they can help with any concerns.'
      ),
    },
  ];

  return (
    <section aria-labelledby='pbb-faq-title'>
      <SectionTitle tag='h2' size='large' id='pbb-faq-title'>
        {t('dutchiePay.signup.faq.title', 'FAQs')}
      </SectionTitle>
      <FaqList>
        {faqs.map((faq, index) => (
          <FaqItem key={faq.question}>
            <StyledAccordion
              square
              elevation={0}
              expanded={openIndex === index}
              onChange={(_event, isExpanded) => setOpenIndex(isExpanded ? index : null)}
            >
              <StyledSummary
                id={`pbb-faq-button-${index}`}
                aria-controls={`pbb-faq-panel-${index}`}
                expandIcon={<ChevronIcon width={14} height={9} />}
              >
                <Question tag='span' size='small'>
                  {faq.question}
                </Question>
              </StyledSummary>
              <StyledDetails>
                <Answer tag='span' size='small'>
                  {faq.answer}
                </Answer>
              </StyledDetails>
            </StyledAccordion>
          </FaqItem>
        ))}
      </FaqList>
    </section>
  );
}

const SectionTitle = styled(Typography.Title)`
  display: block;
  margin: 0 0 8px 0;
  color: var(--dcm-pbb-faq-title-color, ${({ theme }) => theme.colors.dutchiePayBlack});
`;

const FaqList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const FaqItem = styled.li`
  border-bottom: 1px solid var(--dcm-pbb-faq-divider-color, ${({ theme }) => theme.colors.grey[90]});
`;

const StyledAccordion = styled(Accordion)`
  &.MuiPaper-root {
    background: none;
    box-shadow: none;
    border: none;
    color: inherit;
  }

  &.MuiAccordion-root {
    &::before {
      display: none;
    }

    &.Mui-expanded {
      margin: 0;
    }
  }
`;

const StyledSummary = styled(AccordionSummary)`
  &.MuiAccordionSummary-root {
    min-height: unset;
    padding: 16px 0;

    &.Mui-expanded {
      min-height: unset;
    }
  }

  .MuiAccordionSummary-content {
    margin: 0;

    &.Mui-expanded {
      margin: 0;
    }
  }

  .MuiAccordionSummary-expandIcon {
    color: var(--dcm-pbb-faq-chevron-color, ${({ theme }) => theme.colors.grey[40]});
  }
`;

const Question = styled(Typography.Title)`
  color: var(--dcm-pbb-faq-question-color, ${({ theme }) => theme.colors.grey[40]});
  font-weight: 600;
`;

const StyledDetails = styled(AccordionDetails)`
  &.MuiAccordionDetails-root {
    display: block;
    padding: 0 0 16px 0;
  }
`;

const Answer = styled(Typography.Body)`
  display: block;
  color: var(--dcm-pbb-faq-answer-color, ${({ theme }) => theme.colors.grey[35]});
`;
